import { doc, addDoc, collection, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';

export interface NotificationData {
    title: string;
    body: string;
    type: 'invoice' | 'payment' | 'status_change' | 'assignment' | 'approval';
    invoiceNumber?: string;
    clientName?: string;
    totalAmount?: number;
    status?: string;
    action?: 'created' | 'updated';
    createdAt: string;
    read: boolean;
}

export interface FCMNotificationPayload {
    to: string;
    notification: {
        title: string;
        body: string;
    };
    data?: {
        type: string;
        invoiceNumber?: string;
        clientName?: string;
        totalAmount?: string;
        status?: string;
        action?: string;
    };
}

class NotificationService {
    /**
     * Send FCM notification to a single device
     * Uses CORS proxy for development environment
     */
    private async sendFCMNotification(payload: FCMNotificationPayload): Promise<boolean> {
        try {
            const fcmUrl = process.env.REACT_APP_FCM_ENDPOINT;

            if (!fcmUrl) {
                return false;
            }

            const response = await fetch(fcmUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload),
            });

            if (!response.ok) {
                return false;
            }

            await response.json();
            return true;
        } catch (error: any) {
            return false;
        }
    }

    /**
     * Get FCM tokens for a company
     */
    private async getFCMTokens(companyId: string): Promise<string[]> {
        try {
            const companyRef = doc(db, 'companies', companyId);
            const companyDoc = await getDoc(companyRef);

            if (!companyDoc.exists()) {
                return [];
            }

            const companyData = companyDoc.data();
            const tokens: string[] = [];

            // Get FCM tokens from company document
            // FCM tokens can be stored as:
            // - fcmTokens: string[] (array of tokens)
            // - fcmToken: string (single token)
            // - devices: { token: string, deviceId: string }[] (array of device objects)
            if (companyData.fcmTokens && Array.isArray(companyData.fcmTokens)) {
                const validTokens = companyData.fcmTokens.filter((t: string) => t && t.trim());
                tokens.push(...validTokens);
            } else if (companyData.fcmToken && typeof companyData.fcmToken === 'string') {
                tokens.push(companyData.fcmToken);
            } else if (companyData.devices && Array.isArray(companyData.devices)) {
                companyData.devices.forEach((device: any) => {
                    if (device.token && typeof device.token === 'string') {
                        tokens.push(device.token);
                    }
                });
            }

            return tokens.filter((t) => t && t.trim());
        } catch (error: any) {
            return [];
        }
    }

    /**
     * Save notification to database
     * Structure: notifications/companies/{companyId}
     */
    private async saveNotification(
        companyId: string,
        notificationData: NotificationData
    ): Promise<void> {
        try {
            const notificationRef = collection(db, 'notifications', 'companies', companyId);
            await addDoc(notificationRef, notificationData);
        } catch (error: any) {
            // Silent fail - notification saving shouldn't break the flow
        }
    }

    /**
     * Send notification to company
     */
    async sendCompanyNotification(
        companyId: string,
        notificationData: NotificationData
    ): Promise<boolean> {
        try {
            // Get company FCM tokens
            const fcmTokens = await this.getFCMTokens(companyId);

            if (fcmTokens.length === 0) {
                await this.saveNotification(companyId, notificationData);
                return false;
            }

            // Save notification to database
            await this.saveNotification(companyId, notificationData);

            // Send FCM notification to all tokens
            let successCount = 0;
            for (const token of fcmTokens) {
                const payload: FCMNotificationPayload = {
                    to: token,
                    notification: {
                        title: notificationData.title,
                        body: notificationData.body,
                    },
                    data: {
                        type: notificationData.type,
                        invoiceNumber: notificationData.invoiceNumber || '',
                        clientName: notificationData.clientName || '',
                        totalAmount: notificationData.totalAmount?.toString() || '',
                        status: notificationData.status || '',
                        action: notificationData.action || '',
                    },
                };

                const fcmResult = await this.sendFCMNotification(payload);
                if (fcmResult) {
                    successCount++;
                }
            }

            if (successCount > 0) {
                return true;
            } else {
                return false;
            }
        } catch (error: any) {
            try {
                await this.saveNotification(companyId, {
                    ...notificationData,
                    createdAt: new Date().toISOString(),
                });
            } catch (saveError) {
                // Silent fail
            }
            return false;
        }
    }

    /**
     * Send invoice notification to company
     */
    async sendInvoiceNotification(
        companyId: string,
        invoiceNumber: string,
        action: 'created' | 'updated',
        invoiceData?: {
            clientName?: string;
            totalAmount?: number;
            status?: string;
        }
    ): Promise<boolean> {
        try {
            const title = action === 'created'
                ? 'New Invoice Created'
                : 'Invoice Updated';

            const body = action === 'created'
                ? `Invoice ${invoiceNumber} has been created${invoiceData?.clientName ? ` for ${invoiceData.clientName}` : ''}`
                : `Invoice ${invoiceNumber} has been updated${invoiceData?.status ? ` - Status: ${invoiceData.status}` : ''}`;

            const notificationData: NotificationData = {
                title,
                body,
                type: 'invoice',
                invoiceNumber,
                clientName: invoiceData?.clientName,
                totalAmount: invoiceData?.totalAmount,
                status: invoiceData?.status,
                action,
                createdAt: new Date().toISOString(),
                read: false,
            };

            const result = await this.sendCompanyNotification(companyId, notificationData);
            return result;
        } catch (error: any) {
            return false;
        }
    }

    /**
     * Send payment notification to company
     */
    async sendPaymentNotification(
        companyId: string,
        invoiceNumber: string,
        amount: number,
        clientName?: string
    ): Promise<boolean> {
        const notificationData: NotificationData = {
            title: 'Payment Received',
            body: `Payment of ${amount} received for invoice ${invoiceNumber}${clientName ? ` from ${clientName}` : ''}`,
            type: 'payment',
            invoiceNumber,
            clientName,
            totalAmount: amount,
            createdAt: new Date().toISOString(),
            read: false,
        };

        return await this.sendCompanyNotification(companyId, notificationData);
    }

    /**
     * Send test notification to company
     */
    async sendTestNotification(
        companyId: string,
        message?: string
    ): Promise<boolean> {
        const notificationData: NotificationData = {
            title: 'Test Notification',
            body: message || `This is a test notification. FCM is working correctly!`,
            type: 'status_change',
            status: 'test',
            createdAt: new Date().toISOString(),
            read: false,
        };

        return await this.sendCompanyNotification(companyId, notificationData);
    }
}

export const notificationService = new NotificationService();

// Export convenience functions for backward compatibility
export const sendFCMNotification = async (
    companyId: string,
    title: string,
    body: string,
    data?: Record<string, any>
): Promise<void> => {
    const notificationData: NotificationData = {
        title,
        body,
        type: data?.type || 'status_change',
        invoiceNumber: data?.invoiceNumber,
        clientName: data?.clientName,
        totalAmount: data?.totalAmount,
        status: data?.status,
        action: data?.action,
        createdAt: new Date().toISOString(),
        read: false,
    };

    await notificationService.sendCompanyNotification(companyId, notificationData);
};

export const sendInvoiceNotification = async (
    companyId: string,
    invoiceNumber: string,
    action: 'created' | 'updated',
    invoiceData?: {
        clientName?: string;
        totalAmount?: number;
        status?: string;
    }
): Promise<void> => {
    await notificationService.sendInvoiceNotification(companyId, invoiceNumber, action, invoiceData);
};
